import { useCallback } from "react";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { ScanFileUploadTaskContext, MultiUploadedFileResponse, UploadElementType } from "@custom-types/file-upload-types";
import { SdbProject } from "@custom-types/project-types";
import { useAddScansToRevisionAndMerge } from "@hooks/data-management/use-add-scans-to-revision-and-merge";
import { useCancelRevision } from "@hooks/data-management/use-cancel-revision";
import { useHandleAllUploadsFailed } from "@hooks/data-management/use-handle-all-uploads-failed";
import { useUploadFilesTrackFinish } from "@hooks/data-management/use-upload-files-track-finish";
import { COOKIES_ERROR_MESSAGE } from "@utils/core-file-uploader";
import { useAppDispatch } from "@store/store-helper";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { fetchCaptureTreeData } from "@hooks/data-management/use-data-management";
import { delay } from "@utils/time-utils";
import { assert } from "@faro-lotv/foundation";

type ReturnFunction = (
  uploadedResponse: MultiUploadedFileResponse,
  context: ScanFileUploadTaskContext
) => Promise<void>;

/** Callback after all files were uploaded or canceled. */
export function useOnUploadComplete(project: SdbProject, elementType: UploadElementType): ReturnFunction {
  const uploadFilesTrackFinish = useUploadFilesTrackFinish();
  const handleAllUploadsFailed = useHandleAllUploadsFailed();
  const cancelRevision = useCancelRevision();
  const addScansToRevisionAndMerge = useAddScansToRevisionAndMerge();
  const projectApiClient = useProjectApiClient({ projectId: project.id });
  const progressApiClient = useProgressApiClient({ projectId: project.id });
  const dispatch = useAppDispatch();

  return useCallback(
    async (
      uploadedResponse: MultiUploadedFileResponse,
      context: ScanFileUploadTaskContext
    ): Promise<void> => {
      await uploadFilesTrackFinish(uploadedResponse, elementType);

      // The previous code used context.projectId for creating the ProjectAPI client.
      assert(context.projectId === project.id, "Project ID mismatch");

      const successfulUploads = uploadedResponse.successful;
      const failedUploads = uploadedResponse.failed;
      const canceledUploads = uploadedResponse.canceled;
      const areCookiesToBlame = failedUploads.some((failedUpload) =>
        failedUpload.errorMessage === COOKIES_ERROR_MESSAGE
      );

      // Handle case when there are zero successful uploads
      if (!successfulUploads.length) {
        // If there are failed uploads consider it an all-failed case
        if (failedUploads.length) {
          return handleAllUploadsFailed(
            projectApiClient, context.captureTreeRevisionId, areCookiesToBlame, failedUploads[0].errorMessage
          );
        }

        // If there are zero failed uploads and some canceled uploads consider it an all-canceled case
        if (!failedUploads.length && canceledUploads.length) {
          await cancelRevision(projectApiClient, context.captureTreeRevisionId);
          return;
        }
      }

      // Handle case when all uploads were successful.
      if (successfulUploads.length && !failedUploads.length) {
        const canceledImport = !(await addScansToRevisionAndMerge(
          successfulUploads,
          failedUploads,
          projectApiClient,
          context
        ));
        if (canceledImport) {
          return;
        }

        // Fetch the new state immediately instead of waiting until the next usePolling() happens.
        // Unfortunatelly, it seems that background tasks are not created immediately so we won't fetch
        // them if we don't wait a bit. 1-3s wasn't always enough so we wait 5s to be on the safe side.
        // We could add smarter polling but it's not worth the effort.
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- 5 seconds
        await delay(5000);
        try {
          await fetchCaptureTreeData(dispatch, projectApiClient, progressApiClient);
        } catch { /* Not essential. The UI will update a bit later. */ }
      }
    },
    [
      addScansToRevisionAndMerge,
      cancelRevision,
      dispatch,
      elementType,
      handleAllUploadsFailed,
      progressApiClient,
      project.id,
      projectApiClient,
      uploadFilesTrackFinish,
    ]
  );
}
